import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The check against robots (Cloudflare Turnstile), for projects that ask for one. It is loaded from
 * Cloudflare when a form that needs it is on screen, never at start-up, and only when the build has
 * a site key. Without a key there is no widget and nothing is asked of the player.
 *
 * Not exercised against Cloudflare yet: that needs a site key (docs/STATUS.md). The tests run it
 * against a stand-in `window.turnstile`.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      theme?: 'dark' | 'light' | 'auto';
      language?: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    },
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('turnstile missing'));
    };
    script.onerror = () => {
      loading = null;
      reject(new Error('turnstile did not load'));
    };
    document.head.append(script);
  });
  return loading;
}

export interface Captcha {
  /** The widget to put in the form (null when no check is asked for). */
  widget: ReactNode;
  /** The token to send with the request; undefined when there is no check. */
  token: string | undefined;
  /** False while a check is asked for and not yet passed: the form should wait. */
  ready: boolean;
  /** A token is good for one request: call this after using it. */
  reset: () => void;
}

export function useCaptcha(siteKey: string | null): Captcha {
  const { i18n, t } = useTranslation('account');
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!siteKey || !container.current) return;
    const el = container.current;
    let cancelled = false;
    void loadTurnstile().then(
      (api) => {
        if (cancelled) return;
        widgetId.current = api.render(el, {
          sitekey: siteKey,
          theme: 'dark',
          language: i18n.language,
          callback: (value) => {
            setToken(value);
            setFailed(false);
          },
          'expired-callback': () => {
            setToken(null);
          },
          'error-callback': () => {
            setToken(null);
            setFailed(true);
          },
        });
      },
      () => {
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
      if (widgetId.current !== null) window.turnstile?.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [siteKey, i18n.language]);

  const reset = useCallback(() => {
    setToken(null);
    if (widgetId.current !== null) window.turnstile?.reset(widgetId.current);
  }, []);

  if (!siteKey) return { widget: null, token: undefined, ready: true, reset };
  return {
    widget: (
      <div>
        <div ref={container} data-testid="captcha" />
        {failed ? <p role="alert">{t('errors.captchaFailed')}</p> : null}
      </div>
    ),
    token: token ?? undefined,
    ready: token !== null,
    reset,
  };
}
